import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/firebaseConfig';
import {
  subscriptionDocGrantsPremiumAccess,
  partnerProUntilGrantsPremiumAccess,
} from '../utils/subscriptionEntitlement';

// Define query keys
export const USER_PROFILE_QUERY_KEY = 'userProfile';

/**
 * @param {Record<string, unknown>|null|undefined} userData
 * @returns {string|null}
 */
export function selectPrimaryRoleId(userData) {
  const value = userData?.primaryRoleId;
  return value != null && String(value).trim() ? String(value).trim() : null;
}

/**
 * Custom hook for fetching and caching user profile data
 * @param {string} userId - The user ID to fetch profile data for
 * @returns {Object} - Query result containing user data, loading state, and error state
 */
export const useUserProfile = (userId) => {
  const uid = userId || (auth.currentUser ? auth.currentUser.uid : null);

  return useQuery({
    queryKey: [USER_PROFILE_QUERY_KEY, uid],
    queryFn: async () => {
      if (!uid) {
        throw new Error('No authenticated user');
      }

      // First check if user document exists by UID (direct document ID)
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);

      let userDocId;
      let userDocData;

      if (userDocSnap.exists()) {
        // Document exists with user's UID as the document ID
        userDocId = userDocSnap.id;
        userDocData = userDocSnap.data();
      } else {
        // If not found by UID, check if there's a document with the user's email
        if (auth.currentUser?.email) {
          const usersRef = collection(db, 'users');
          const userEmailQuery = query(usersRef, where('email', '==', auth.currentUser.email));
          const userEmailSnapshot = await getDocs(userEmailQuery);

          if (!userEmailSnapshot.empty) {
            const userDoc = userEmailSnapshot.docs[0];
            userDocId = userDoc.id;
            userDocData = userDoc.data();
          }
        }
        // New user: no document yet — return defaults so app can load
        if (!userDocId) {
          userDocId = uid;
          userDocData = {};
        }
      }

      // User cookbooks live under users/{uid}/decks (see firestore.rules)
      const decksRef = collection(db, `users/${uid}/decks`);
      const decksSnapshot = await getDocs(decksRef);

      const decks = [];
      decksSnapshot.forEach(doc => {
        decks.push({ id: doc.id, ...doc.data() });
      });

      let isPremium = false;
      const now = new Date();
      if (partnerProUntilGrantsPremiumAccess(userDocData.partnerProUntil, now)) {
        isPremium = true;
      }
      try {
        const subscriptionsRef = collection(db, `users/${uid}/subscriptions`);
        const subscriptionsSnapshot = await getDocs(subscriptionsRef);
        if (!subscriptionsSnapshot.empty) {
          for (const subDoc of subscriptionsSnapshot.docs) {
            if (subscriptionDocGrantsPremiumAccess(subDoc.data(), now)) {
              isPremium = true;
              break;
            }
          }
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }

      return {
        userData: {
          ...userDocData,
          uid,
          docId: userDocId
        },
        userDecks: decks,
        isPremium
      };
    },
    enabled: !!uid, // Only run the query if we have a userId
    staleTime: 60 * 60 * 1000, // Consider data fresh for 60 minutes
    cacheTime: 24 * 60 * 60 * 1000, // Keep data in cache for 24 hours
  });
};

/**
 * Function to prefetch user profile data
 * @param {Object} queryClient - The React Query client instance
 * @param {string} userId - The user ID to prefetch data for
 * @returns {Promise} - Promise that resolves when prefetching is complete
 */
export const prefetchUserProfile = async (queryClient, userId) => {
  if (!userId) {
    console.error('Cannot prefetch user profile: No user ID provided');
    return;
  }

  // Prefetch both profile and stats data
  await queryClient.prefetchQuery({
    queryKey: [USER_PROFILE_QUERY_KEY, userId],
    queryFn: async () => {
      // First check if user document exists by UID (direct document ID)
      const userDocRef = doc(db, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);

      let userDocId;
      let userDocData;

      if (userDocSnap.exists()) {
        // Document exists with user's UID as the document ID
        userDocId = userDocSnap.id;
        userDocData = userDocSnap.data();
      } else {
        // If not found by UID, check if there's a document with the user's email
        const currentUser = auth.currentUser;
        if (currentUser?.email) {
          const usersRef = collection(db, 'users');
          const userEmailQuery = query(usersRef, where('email', '==', currentUser.email));
          const userEmailSnapshot = await getDocs(userEmailQuery);

          if (!userEmailSnapshot.empty) {
            const userDoc = userEmailSnapshot.docs[0];
            userDocId = userDoc.id;
            userDocData = userDoc.data();
          }
        }
        // New user: no document yet — use defaults so auth flow can complete
        if (!userDocId) {
          userDocId = userId;
          userDocData = {};
        }
      }

      const decksRef = collection(db, `users/${userId}/decks`);
      const decksSnapshot = await getDocs(decksRef);

      const decks = [];
      decksSnapshot.forEach(doc => {
        decks.push({ id: doc.id, ...doc.data() });
      });

      let isPremium = false;
      const now = new Date();
      if (partnerProUntilGrantsPremiumAccess(userDocData.partnerProUntil, now)) {
        isPremium = true;
      }
      try {
        const subscriptionsRef = collection(db, `users/${userId}/subscriptions`);
        const subscriptionsSnapshot = await getDocs(subscriptionsRef);

        if (!subscriptionsSnapshot.empty) {
          for (const d of subscriptionsSnapshot.docs) {
            if (subscriptionDocGrantsPremiumAccess(d.data(), now)) {
              isPremium = true;
              break;
            }
          }
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
      }

      return {
        userData: {
          ...userDocData,
          uid: userId,
          docId: userDocId
        },
        userDecks: decks,
        isPremium
      };
    },
    staleTime: 60 * 60 * 1000, // Consider data fresh for 60 minutes
  });
};

/**
 * Hook to get the React Query client instance
 * @returns {Object} - The React Query client instance
 */
export const useUserProfilePrefetch = () => {
  const queryClient = useQueryClient();

  return {
    prefetchUserProfile: (userId) => prefetchUserProfile(queryClient, userId)
  };
};
